import { api } from "../api.js";
import { el } from "./dom.js";
import { leaderboardPanel } from "./leaderboard.js";
import { tilesPanel } from "./lobbyTiles.js";
import { isMobile } from "../main.js";
import type { MatchmadeMode, MeResponse } from "@webolo/shared";

const ADMIN_USERNAME = "bolo";

// Lobby root. Four big tiles drive the primary flow (FFA / Teams / Siege /
// Custom Game). Custom Game is hidden on mobile — the tilesPanel renders the
// remaining three across the width. The leaderboard sits beside the tiles on
// desktop; mobile stacks vertically via CSS.
//
// onPlay handles joins from the in-tile Custom Game expansion (room list +
// create form); the matchmaker tiles route through onMatchmake instead.
export function lobbyScreen(
  me: MeResponse,
  onPlay: (gameId: string, team: number) => void,
  onMatchmake: (mode: MatchmadeMode) => void,
  onLoggedOut: () => void,
  onAdmin?: () => void,
): HTMLElement {
  return el(
    "div",
    { class: "screen lobby" },
    el(
      "div",
      { class: "lby-shell" },
      statsBanner(me, async () => {
        await api.logout();
        onLoggedOut();
      }, onAdmin),
      el(
        "div",
        { class: "lby-grid" },
        tilesPanel({
          onMatchmake,
          onPlay,
          showCustomGame: !isMobile(),
        }),
        leaderboardPanel(me.username),
      ),
      versionsFooter(),
    ),
  );
}

function versionsFooter(): HTMLElement {
  const node = el("div", { class: "lby-versions" }, el("span", { class: "muted" }, "Loading versions…"));
  void api
    .versions()
    .then(({ versions }) => {
      node.replaceChildren(
        el("span", {},
          el("span", { class: "lby-ver-key" }, "Web Client "),
          el("span", { class: "lby-ver-val" }, `v${versions.webClient}`),
        ),
        el("span", { class: "lby-ver-sep" }, "·"),
        el("span", {},
          el("span", { class: "lby-ver-key" }, "Server "),
          el("span", { class: "lby-ver-val" }, `v${versions.server}`),
        ),
      );
    })
    .catch(() => {
      node.replaceChildren();
    });
  return node;
}

function statsBanner(me: MeResponse, onLogout: () => void, onAdmin?: () => void): HTMLElement {
  const isAdmin = me.username.toLowerCase() === ADMIN_USERNAME && onAdmin != null;
  const adminBtn = isAdmin
    ? el("button", { class: "lcard-admin", type: "button", onclick: onAdmin }, "ADMIN")
    : null;
  const kd = me.deaths > 0 ? (me.kills / me.deaths).toFixed(2) : me.kills.toFixed(2);
  return el(
    "section",
    { class: "lcard lcard-stats" },
    el(
      "div",
      { class: "stats-logo" },
      el("span", { class: "logo-we" }, "WE"),
      el("span", { class: "logo-bolo" }, "BOLO"),
    ),
    el(
      "div",
      { class: "stats-who" },
      el("span", { class: "stats-who-lbl" }, "COMMANDER"),
      el("span", { class: "stats-who-name" }, me.username.toUpperCase()),
    ),
    el(
      "div",
      { class: "stats-row" },
      statCell("KILLS", me.kills),
      statCell("DEATHS", me.deaths),
      statCell("K/D", kd),
      statCell("WINS", me.wins),
      statCell("LOSSES", me.losses),
      statCell("GAMES", me.gamesPlayed),
      statCell("BEST WAVE", me.siegeBestRound),
    ),
    adminBtn,
    el(
      "button",
      { class: "lcard-logout", type: "button", onclick: onLogout },
      "LOG OUT",
    ),
  );
}

function statCell(label: string, value: number | string): HTMLElement {
  return el(
    "div",
    { class: "stat-cell" },
    el("span", { class: "stat-val" }, String(value)),
    el("span", { class: "stat-key" }, label),
  );
}
